import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Copy, MapPin, Phone, PhoneIncoming } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADDRESS = "New Bypass Road, New Zero Mile, Patna, Bihar 800030";

export const Contact = () => {
  const [copied, setCopied] = useState(false);
  const [cbPhone, setCbPhone] = useState("");
  const [cbDone, setCbDone] = useState(false);
  const [cbError, setCbError] = useState("");
  const [cbBusy, setCbBusy] = useState(false);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(ADDRESS);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const submitCallback = async (e) => {
    e.preventDefault();
    setCbError("");
    setCbBusy(true);
    try {
      await axios.post(`${API}/callback`, { phone: cbPhone });
      setCbDone(true);
    } catch {
      setCbError("Couldn't send right now — please call us directly.");
    } finally {
      setCbBusy(false);
    }
  };

  return (
    <section className="section contact-section" id="contact">
      <div className="container">
        <motion.div className="contact-heading"
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="overline">Talk to a real human</p>
          <h2>One call and <em>we're rolling.</em></h2>
        </motion.div>
        <div className="contact-grid">
          <motion.a className="contact-card" data-testid="contact-phone-primary" href="tel:+919135667888"
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="contact-card-icon"><Phone size={19} /></span>
            <small>Call the moving desk</small>
            <strong>91356 67888</strong>
            <em>Tap to call</em>
          </motion.a>
          <motion.a className="contact-card" data-testid="contact-phone-secondary" href="tel:+919135668888"
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08, duration: 0.6 }}>
            <span className="contact-card-icon"><Phone size={19} /></span>
            <small>Alternate line</small>
            <strong>91356 68888</strong>
            <em>Tap to call</em>
          </motion.a>
          <motion.div className="contact-card address-card" data-testid="contact-address-card"
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.16, duration: 0.6 }}>
            <span className="contact-card-icon"><MapPin size={19} /></span>
            <small>Visit our Patna hub</small>
            <strong className="address-text" data-testid="footer-address">New Bypass Road, New Zero Mile<br />Patna, Bihar 800030</strong>
            <button className="copy-btn" data-testid="copy-address-button" onClick={copyAddress}>
              {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy address</>}
            </button>
          </motion.div>
        </div>
        <motion.form className="callback-strip" data-testid="callback-form" onSubmit={submitCallback}
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2, duration: 0.6 }}>
          <span className="callback-icon"><PhoneIncoming size={20} /></span>
          <div className="callback-copy">
            <strong>Too busy to talk right now?</strong>
            <small>Leave your number — we'll call you back for free.</small>
          </div>
          {cbDone ? (
            <span className="callback-done" data-testid="callback-success"><Check size={16} /> Done! Our team will ring you back shortly.</span>
          ) : (
            <div className="callback-controls">
              <input data-testid="callback-phone-input" required value={cbPhone} onChange={(e) => setCbPhone(e.target.value)} placeholder="Your mobile number" aria-label="Your mobile number" />
              <button className="btn btn-gold btn-sm" data-testid="callback-submit-button" type="submit" disabled={cbBusy}>{cbBusy ? "Sending…" : "Call me back"}</button>
            </div>
          )}
          {cbError && <span className="callback-error" data-testid="callback-error">{cbError}</span>}
        </motion.form>
        <motion.div className="map-card" data-testid="map-card"
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <iframe
            title="Jay Maa Sherawali Patna hub on Google Maps"
            data-testid="google-map-embed"
            src="https://www.google.com/maps?q=New+Bypass+Road,+New+Zero+Mile,+Patna,+Bihar+800030&output=embed"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="map-info">
            <div>
              <small>OUR HUB</small>
              <strong>New Bypass Road, New Zero Mile, Patna, Bihar 800030</strong>
            </div>
            <a data-testid="open-in-maps-link" href="https://www.google.com/maps/search/?api=1&query=New+Bypass+Road+New+Zero+Mile+Patna+Bihar+800030" target="_blank" rel="noopener noreferrer">
              Open in Google Maps
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
